/* Reya · header behaviour: transparent/scrolled state, header height var, hover-intent mega menus,
   accordion drawer, theme-editor block selection. */
(() => {
  if (window.__rrHeader) return;
  window.__rrHeader = true;

  const init = () => {
    const wrap = document.querySelector('[data-rr-header]');
    if (!wrap) return;
    const root = document.documentElement;

    /* Header height (used by the transparent header when hidden on scroll) */
    const setH = () => root.style.setProperty('--rr-header-h', `${wrap.offsetHeight}px`);
    setH();
    if ('ResizeObserver' in window) {
      wrap.__rrRO && wrap.__rrRO.disconnect();
      wrap.__rrRO = new ResizeObserver(setH);
      wrap.__rrRO.observe(wrap);
    }

    /* Scroll state */
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const top = window.scrollY < 8;
        wrap.classList.toggle('is-top', top && wrap.classList.contains('rr-transparent'));
        wrap.classList.toggle('is-scrolled', !top);
        ticking = false;
      });
    };
    window.removeEventListener('scroll', window.__rrHeaderScroll || (() => {}));
    window.__rrHeaderScroll = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* Solid header while any menu / search is open (toggle does not bubble → capture) */
    const syncOpen = () =>
      wrap.classList.toggle('rr-menu-open', !!wrap.querySelector('details[open], rr-mega-item.is-open'));
    wrap.addEventListener('rr:mega', syncOpen);
    wrap.addEventListener('toggle', syncOpen, true);

    /* Hover-intent for desktop menus (click + keyboard keep working) */
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const menus = [...wrap.querySelectorAll('.rr-nav header-menu')];
    const closeMenu = (details) => {
      if (!details.open) return;
      details.open = false;
      delete details.dataset.rrHover;
      const s = details.querySelector('summary');
      s && s.setAttribute('aria-expanded', 'false');
    };
    if (wrap.hasAttribute('data-rr-hover')) {
      menus.forEach((menu) => {
        const details = menu.querySelector('details');
        const summary = details && details.querySelector('summary');
        if (!summary) return;
        let t;
        menu.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse' || !fine.matches) return;
          clearTimeout(t);
          t = setTimeout(() => {
            menus.forEach((m) => m !== menu && closeMenu(m.querySelector('details')));
            if (!details.open) {
              details.open = true;
              details.dataset.rrHover = '1';
              summary.setAttribute('aria-expanded', 'true');
            }
          }, 90);
        });
        menu.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(t);
          t = setTimeout(() => details.dataset.rrHover && closeMenu(details), 180);
        });
        summary.addEventListener('click', (e) => {
          if (details.dataset.rrHover && details.open) {
            e.preventDefault();
            delete details.dataset.rrHover; // pin it open
          }
        });
      });
    }
    wrap.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = e.target.closest && e.target.closest('.rr-nav details[open]');
      if (!open) return;
      closeMenu(open);
      const s = open.querySelector('summary');
      s && s.focus();
    });

    /* Drawer: accordion submenus. Stop Dawn's MenuDrawer slide-panel handlers (capture phase). */
    const drawer = wrap.querySelector('header-drawer');
    if (drawer && !drawer.__rrAcc) {
      drawer.__rrAcc = true;
      drawer.addEventListener(
        'click',
        (e) => {
          const sum = e.target.closest('summary.rr-acc__summary');
          if (sum) {
            e.stopPropagation();
            const details = sum.parentElement;
            // one open at a time per level
            if (!details.open) {
              const list = details.closest('ul');
              list &&
                list.querySelectorAll(':scope > li > details.rr-acc[open]').forEach((d) => d !== details && (d.open = false));
            }
            requestAnimationFrame(() => sum.setAttribute('aria-expanded', details.open ? 'true' : 'false'));
            return;
          }
          if (e.target.closest('.rr-drawer__search button, .rr-drawer__quick, .rr-drawer__promos')) e.stopPropagation();
        },
        true
      );
      drawer.querySelectorAll('summary.rr-acc__summary').forEach((s) => s.setAttribute('aria-expanded', 'false'));
    }
  };

  /* Theme editor: open the menu panel that contains a selected promo block */
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    const details = el && el.closest && el.closest('.rr-nav details');
    if (details) {
      details.open = true;
      delete details.dataset.rrHover;
    }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const details = e.target && e.target.closest && e.target.closest('.rr-nav details');
    if (details) details.open = false;
  });
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-rr-header]')) init();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* Reya · announcement bar rotator */
(() => {
  if (customElements.get('rr-announce')) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  customElements.define(
    'rr-announce',
    class extends HTMLElement {
      connectedCallback() {
        this.items = [...this.querySelectorAll('.rr-announce__item')];
        if (this.items.length < 2) return;
        this.index = 0;
        this.speed = Math.max(3, parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.autoplay = this.dataset.autoplay === 'true' && !reduce;
        this.paused = false;
        this.querySelectorAll('[data-rr-dir]').forEach((b) =>
          b.addEventListener('click', () => {
            this.go(this.index + parseInt(b.dataset.rrDir, 10));
            this.restart();
          })
        );
        const pause = () => (this.paused = true);
        const resume = () => (this.paused = false);
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.addEventListener('shopify:block:select', (e) => {
          const i = this.items.indexOf(e.target.closest('.rr-announce__item'));
          if (i > -1) this.go(i);
          this.paused = true;
        });
        this.addEventListener('shopify:block:deselect', resume);
        this.restart();
      }
      disconnectedCallback() {
        clearInterval(this.timer);
      }
      restart() {
        clearInterval(this.timer);
        if (!this.autoplay) return;
        this.timer = setInterval(() => !this.paused && !document.hidden && this.go(this.index + 1), this.speed);
      }
      go(i) {
        const n = this.items.length;
        i = ((i % n) + n) % n;
        if (i === this.index) return;
        const prev = this.items[this.index];
        this.items.forEach((el) => el.classList.remove('is-prev'));
        prev.classList.remove('is-active');
        prev.classList.add('is-prev');
        this.items[i].classList.add('is-active');
        this.index = i;
      }
    }
  );
})();

/* Reya · block-driven mega menu item: link + toggle button + panel.
   Hover-intent (mouse), click/Enter on the toggle, first tap opens on touch, Esc closes, focus-out closes. */
(() => {
  if (customElements.get('rr-mega-item')) return;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  customElements.define(
    'rr-mega-item',
    class extends HTMLElement {
      connectedCallback() {
        this.link = this.querySelector(':scope > a');
        this.toggle = this.querySelector(':scope > .rr-nav__toggle');
        this.panel = this.querySelector(':scope > .rr-mp');
        this.wrap = this.closest('[data-rr-header]');
        if (!this.panel || !this.toggle) return;
        const hover = this.wrap && this.wrap.hasAttribute('data-rr-hover');
        this.addEventListener('pointerenter', (e) => {
          if (!hover || e.pointerType !== 'mouse' || !fine.matches) return;
          clearTimeout(this.t);
          this.t = setTimeout(() => this.open(), 90);
        });
        this.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          clearTimeout(this.t);
          this.t = setTimeout(() => !this.contains(document.activeElement) && this.close(), 200);
        });
        this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open(true)));
        this.link.addEventListener('click', (e) => {
          if (this.touchOpen && !this.isOpen) {
            e.preventDefault();
            this.open();
          }
        });
        this.link.addEventListener('pointerdown', (e) => (this.touchOpen = e.pointerType === 'touch'));
        this.addEventListener('keydown', (e) => {
          if (e.key !== 'Escape' || !this.isOpen) return;
          e.stopPropagation();
          this.close();
          this.toggle.focus();
        });
        this.addEventListener('focusout', () =>
          setTimeout(() => !this.contains(document.activeElement) && !this.matches(':hover') && this.close())
        );
      }
      get isOpen() {
        return this.classList.contains('is-open');
      }
      // Drop the panel directly under its own menu item, kept inside the viewport.
      place() {
        const card = this.panel.querySelector('.rr-mp__card');
        if (!card || window.innerWidth < 990) return;
        const link = this.querySelector('.rr-nav__link') || this;
        const p = this.panel.getBoundingClientRect();
        const cs = getComputedStyle(this.panel);
        const padL = parseFloat(cs.paddingLeft) || 0;
        const padR = parseFloat(cs.paddingRight) || 0;
        const room = p.width - padL - padR;
        const x = link.getBoundingClientRect().left - p.left - padL - 26;
        card.style.marginLeft = Math.max(0, Math.min(x, room - card.offsetWidth)) + 'px';
      }
      open(focusFirst = false) {
        if (this.isOpen) return;
        document.querySelectorAll('rr-mega-item.is-open').forEach((m) => m !== this && m.close());
        clearTimeout(this.hideT);
        this.panel.hidden = false;
        void this.panel.offsetWidth;
        this.place();
        this.classList.add('is-open');
        this.toggle.setAttribute('aria-expanded', 'true');
        if (this.wrap) {
          this.wrap.preventHide = true;
          this.wrap.dispatchEvent(new CustomEvent('rr:mega'));
        }
        if (focusFirst) {
          const f = this.panel.querySelector('a[href]');
          f && f.focus();
        }
      }
      close() {
        if (!this.isOpen) return;
        this.classList.remove('is-open');
        this.toggle.setAttribute('aria-expanded', 'false');
        this.hideT = setTimeout(() => (this.panel.hidden = true), reduce.matches ? 0 : 260);
        if (this.wrap) {
          this.wrap.preventHide = !!document.querySelector('rr-mega-item.is-open');
          this.wrap.dispatchEvent(new CustomEvent('rr:mega'));
        }
      }
    }
  );
  document.addEventListener('click', (e) => {
    if (!e.target.closest('rr-mega-item')) document.querySelectorAll('rr-mega-item.is-open').forEach((m) => m.close());
  });
  document.addEventListener('shopify:block:select', (e) => {
    const m = e.target.closest && e.target.closest('rr-mega-item');
    if (m) return m.open();
    // blocks shown inside a panel are matched by the panel they render in
    const inPanel = document.querySelector('.rr-mp [data-shopify-editor-block*="' + (e.detail && e.detail.blockId) + '"]');
    inPanel && inPanel.closest('rr-mega-item') && inPanel.closest('rr-mega-item').open();
  });
})();

/* Reya · footer link columns: accordions on mobile, always open on tablet/desktop */
(() => {
  if (window.__rrFooterAcc) return;
  window.__rrFooterAcc = true;
  const mq = window.matchMedia('(min-width: 750px)');
  const sync = () =>
    document.querySelectorAll('details.rr-footer__acc').forEach((d) => {
      d.open = mq.matches;
    });
  document.addEventListener('click', (e) => {
    const sum = e.target.closest && e.target.closest('.rr-footer__acc > summary');
    if (sum && mq.matches) e.preventDefault();
  });
  mq.addEventListener('change', sync);
  document.addEventListener('shopify:section:load', sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();

/* Reya · recently viewed header menu — reads the `rr:recent` handles written on product pages
   (assets/rr-product.js) and fetches product JSON only when the panel is first opened. */
(() => {
  if (customElements.get('rr-recent-menu')) return;
  const KEY = 'rr:recent';
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const read = () => {
    try { return (JSON.parse(localStorage.getItem(KEY)) || []).filter(Boolean); } catch (e) { return []; }
  };
  const write = (l) => {
    try { localStorage.setItem(KEY, JSON.stringify(l)); } catch (e) {}
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
    } catch (e) { return (cents / 100).toFixed(2); }
  };
  const sized = (src, w) => {
    if (!src) return '';
    const u = src.startsWith('//') ? `https:${src}` : src;
    return u + (u.includes('?') ? '&' : '?') + `width=${w}`;
  };

  customElements.define(
    'rr-recent-menu',
    class extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-rr-recent-toggle]');
        this.panel = this.querySelector('.rr-recent__panel');
        this.list = this.querySelector('[data-rr-recent-list]');
        this.clearBtn = this.querySelector('[data-rr-recent-clear]');
        this.limit = +this.dataset.limit || 5;
        this.loadedKey = null;
        this.syncCount();
        this.btn.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
        this.clearBtn && this.clearBtn.addEventListener('click', () => { write([]); this.render([]); this.syncCount(); });
        this.onDoc = (e) => { if (!this.contains(e.target)) this.close(); };
        this.onKey = (e) => { if (e.key === 'Escape') { this.close(); this.btn.focus(); } };
        window.addEventListener('storage', (e) => e.key === KEY && this.syncCount());
      }
      syncCount() {
        const n = read().length;
        const c = this.querySelector('[data-rr-recent-count]');
        if (c) { c.textContent = n ? Math.min(n, 99) : ''; c.dataset.count = n; }
      }
      open() {
        this.panel.hidden = false;
        this.btn.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        document.addEventListener('click', this.onDoc);
        document.addEventListener('keydown', this.onKey);
        this.load();
      }
      close() {
        if (this.panel.hidden) return;
        this.panel.hidden = true;
        this.btn.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        document.removeEventListener('click', this.onDoc);
        document.removeEventListener('keydown', this.onKey);
      }
      async load() {
        const handles = read().slice(0, this.limit);
        const key = handles.join('|');
        if (key === this.loadedKey) return;
        this.loadedKey = key;
        if (!handles.length) return this.render([]);
        const items = await Promise.all(
          handles.map((h) =>
            fetch(`${root}products/${encodeURIComponent(h)}.js`)
              .then((r) => (r.ok ? r.json() : { _bad: h }))
              .catch(() => null)
          )
        );
        const bad = items.filter((p) => p && p._bad).map((p) => p._bad);
        if (bad.length) { write(read().filter((h) => !bad.includes(h))); this.syncCount(); }
        this.render(items.filter((p) => p && !p._bad));
      }
      render(products) {
        this.classList.toggle('has-items', products.length > 0);
        if (this.clearBtn) this.clearBtn.hidden = !products.length;
        this.list.innerHTML = products
          .map((p) => {
            const img = p.featured_image
              ? `<img class="rr-recent__img" src="${esc(sized(p.featured_image, 120))}" alt="" width="52" height="52" loading="lazy">`
              : '<span class="rr-recent__img" aria-hidden="true"></span>';
            return `<li><a class="rr-recent__link" href="${esc(p.url)}">${img}<span><span class="rr-recent__name">${esc(p.title)}</span><span class="rr-recent__price">${esc(money(p.price))}</span></span></a></li>`;
          })
          .join('');
      }
    }
  );
})();
